import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import {
  BOOLEAN_STATUS_COLORS,
  CurrencyCell,
  DateCell,
  NumberCell,
  StatusCell,
  TextCell,
} from '@/shared/ui';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import type { ResizableGridColDef } from '../persistence/headersApiAdapter';
import { headerTitleStyle } from './useGeneratikTableColumns.styles';

const FIXED_CELL_TYPE_DIMENSIONS: Partial<
  Record<
    NonNullable<GeneratikTableColumn['cellType']>,
    { width: number; minWidth?: number; maxWidth?: number }
  >
> = {
  date: { width: 110, minWidth: 90 },
  status: { width: 150, minWidth: 100 },
  currency: { width: 120, minWidth: 90 },
  number: { width: 120, minWidth: 90 },
  boolean: { width: 80, minWidth: 70 },
  shortText: { width: 140, minWidth: 100 },
  actions: { width: 56, minWidth: 56, maxWidth: 56 },
};

const FLEX_TEXT_MIN_WIDTH = 160;
const FLEX_TEXT_MAX_WIDTH = 400;
// MUI applies this minWidth when a column sets none; authored widths below it
// (icon-sized columns) must keep their own floor or the default would widen them.
const MUI_DEFAULT_COL_MIN_WIDTH = 50;
const LONG_HEADER_RESIZE_FLOOR_PX = 120;

// Header-label width estimate, calibrated to the rendered header
// (`MuiDataGrid-columnHeaderTitle`: 0.875rem / weight 500, header padding 0 0.75rem).
// Used as a min-width floor so the label stays fully visible next to the
// header chrome that can appear at once: the sort arrow with its multi-sort
// priority badge (the badge is absolutely positioned and overhangs the arrow
// button, so it clips first when the header is squeezed) and the column-menu
// button the hover state adds. Allowances match the rendered sizes.
export const HEADER_CHAR_WIDTH_PX = 8; // avg glyph width at 14px medium
export const HEADER_PADDING_PX = 24; // 0.75rem left + 0.75rem right
export const HEADER_SORT_ALLOWANCE_PX = 32; // 24px arrow button + 8px badge overhang
export const HEADER_MENU_ALLOWANCE_PX = 32; // 28px menu button + 4px margin

export interface HeaderChromeOptions {
  sortable?: boolean;
  disableColumnMenu?: boolean;
}

export const estimateHeaderMinWidth = (
  headerName?: string,
  chrome?: HeaderChromeOptions,
): number => {
  if (typeof headerName !== 'string') return 0;
  const label = headerName.trim();
  if (!label) return 0;
  const sortAllowance = chrome?.sortable === false ? 0 : HEADER_SORT_ALLOWANCE_PX;
  const menuAllowance = chrome?.disableColumnMenu === true ? 0 : HEADER_MENU_ALLOWANCE_PX;
  return (
    Math.ceil(label.length * HEADER_CHAR_WIDTH_PX) +
    HEADER_PADDING_PX +
    sortAllowance +
    menuAllowance
  );
};

export const useGeneratikTableColumns = <R extends GridValidRowModel>(
  columns: GeneratikTableColumn<R>[],
  options?: { hideColumnMenu?: boolean },
): GridColDef<R>[] => {
  const { t } = useTranslation();
  const hideColumnMenu = options?.hideColumnMenu ?? false;

  return useMemo((): GridColDef<R>[] => {
    return columns.map((col): GridColDef<R> => {
      const { cellType, statusColors, renderGroupCell, resizeMaxWidth, ...rest } = col;
      const gridColDef: GridColDef<R> = { ...rest } as GridColDef<R>;

      const hasAuthoredFlex = rest.flex !== undefined;
      const dimensions = cellType ? FIXED_CELL_TYPE_DIMENSIONS[cellType] : undefined;
      const baseWidth = rest.width ?? dimensions?.width;
      const isFlexText = !hasAuthoredFlex && baseWidth === undefined;

      if (isFlexText) {
        gridColDef.flex = FLEX_TEXT_MIN_WIDTH;
        gridColDef.maxWidth = gridColDef.maxWidth ?? FLEX_TEXT_MAX_WIDTH;
      } else if (!hasAuthoredFlex && baseWidth !== undefined) {
        if (gridColDef.maxWidth === undefined && dimensions?.maxWidth !== undefined) {
          gridColDef.maxWidth = dimensions.maxWidth;
        }
        // A width-based column keeps its base width as a floor, but grows
        // proportionally (flex is in px scale) so visible columns always cover
        // the grid's full width — including after some columns are hidden.
        const canGrow =
          rest.resizable !== false &&
          (gridColDef.maxWidth === undefined || gridColDef.maxWidth > baseWidth);
        if (canGrow) {
          gridColDef.flex = baseWidth;
          delete gridColDef.width;
        } else {
          gridColDef.width = baseWidth;
        }
      }

      // Floor the min-width so nothing collapses below its content type or its
      // own header label. The base width is deliberately NOT part of the floor:
      // it is only the flex basis. Making it the floor turned every default
      // width into a hard lower resize bound (the resize clamp reads
      // colDef.minWidth), so columns could grow but never be dragged narrower.
      // Never lowers an author-set minWidth and never exceeds maxWidth.
      const isTinyBaseWidth =
        !hasAuthoredFlex &&
        gridColDef.flex !== undefined &&
        baseWidth !== undefined &&
        baseWidth < MUI_DEFAULT_COL_MIN_WIDTH;
      const naturalFloor = Math.max(
        rest.minWidth ?? 0,
        dimensions?.minWidth ?? 0,
        isTinyBaseWidth ? baseWidth : 0,
      );
      const minWidthFloor = Math.max(
        naturalFloor,
        estimateHeaderMinWidth(gridColDef.headerName, {
          sortable: gridColDef.sortable,
          disableColumnMenu: hideColumnMenu || gridColDef.disableColumnMenu,
        }),
      );
      const clampedFloor =
        gridColDef.maxWidth !== undefined
          ? Math.min(minWidthFloor, gridColDef.maxWidth)
          : minWidthFloor;
      if (clampedFloor > 0) {
        gridColDef.minWidth = clampedFloor;
      }
      const resizeFloor = Math.min(
        Math.max(naturalFloor, LONG_HEADER_RESIZE_FLOOR_PX),
        clampedFloor,
        gridColDef.maxWidth ?? Infinity,
      );
      if (resizeFloor < clampedFloor && rest.resizable !== false) {
        (gridColDef as ResizableGridColDef).generatikResizeFloor = resizeFloor;
      }

      if (resizeMaxWidth !== undefined && resizeMaxWidth > (gridColDef.maxWidth ?? Infinity)) {
        (gridColDef as ResizableGridColDef).generatikResizeCeiling = resizeMaxWidth;
      }

      if (!gridColDef.renderCell && cellType) {
        switch (cellType) {
          case 'status':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => {
              const row = params.row as { status?: string; rejectionReason?: string };
              if (row?.status === 'REJECTED' && row?.rejectionReason) {
                return (
                  <Tooltip title={row.rejectionReason} placement="top">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        width: '100%',
                        cursor: 'default',
                      }}
                    >
                      <StatusCell
                        value={params.value}
                        colors={statusColors}
                        label={statusLabel(params)}
                      />
                      <InfoOutlinedIcon
                        sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }}
                      />
                    </Box>
                  </Tooltip>
                );
              }
              return (
                <StatusCell
                  value={params.value}
                  colors={statusColors}
                  label={statusLabel(params)}
                />
              );
            };
            break;

          case 'date':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
              <DateCell value={params.value} />
            );
            break;

          case 'currency':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
              <CurrencyCell value={params.value} testId={`booking-cell-${params.field}`} />
            );
            if (!gridColDef.align) gridColDef.align = 'right';
            if (!gridColDef.headerAlign) gridColDef.headerAlign = 'right';
            break;

          case 'number':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
              <NumberCell value={params.value} />
            );
            if (!gridColDef.align) gridColDef.align = 'right';
            if (!gridColDef.headerAlign) gridColDef.headerAlign = 'right';
            break;

          case 'boolean':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
              <StatusCell
                value={params.value ? t('yes') : t('no')}
                colors={BOOLEAN_STATUS_COLORS}
              />
            );
            break;

          case 'text':
          case 'shortText':
            gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
              <TextCell value={params.value} />
            );
            break;

          default:
            break;
        }
      } else if (!gridColDef.renderCell && !cellType) {
        gridColDef.renderCell = (params: GridRenderCellParams<R>) => (
          <TextCell value={params.value} />
        );
      }

      if (!gridColDef.renderHeader) {
        gridColDef.renderHeader = (params: GridColumnHeaderParams<R>) => (
          <div className="MuiDataGrid-columnHeaderTitle" style={headerTitleStyle}>
            {params.colDef.headerName ?? params.colDef.field}
          </div>
        );
      }

      if (gridColDef.renderCell) {
        const originalRenderCell = gridColDef.renderCell;
        const { field } = gridColDef;
        gridColDef.renderCell = (params: GridRenderCellParams<R>) => {
          if (params.rowNode.type !== 'group') {
            return originalRenderCell(params);
          }
          if (renderGroupCell) {
            return renderGroupCell(params);
          }
          const groupNode = params.rowNode as {
            groupingField?: string | null;
            isAutoGenerated?: boolean;
          };
          if (groupNode.isAutoGenerated) {
            return null;
          }
          if (!groupNode.groupingField) {
            return originalRenderCell(params);
          }
          if (groupNode.groupingField === field) {
            return <TextCell value={params.value} />;
          }
          return null;
        };
      }

      return gridColDef;
    });
  }, [columns, t, hideColumnMenu]);
};

export const getColumnsFitWidth = <R extends GridValidRowModel>(
  columns: GeneratikTableColumn<R>[],
  options?: { hideColumnMenu?: boolean },
): number =>
  columns.reduce((total, column) => {
    const dimensions = column.cellType ? FIXED_CELL_TYPE_DIMENSIONS[column.cellType] : undefined;
    const authoredWidth = column.width ?? dimensions?.width;
    const baseWidth = authoredWidth ?? FLEX_TEXT_MIN_WIDTH;
    const maxWidth =
      column.maxWidth ??
      dimensions?.maxWidth ??
      (authoredWidth === undefined ? FLEX_TEXT_MAX_WIDTH : undefined);
    const floor = Math.max(
      column.minWidth ?? 0,
      dimensions?.minWidth ?? 0,
      estimateHeaderMinWidth(column.headerName, {
        sortable: column.sortable,
        disableColumnMenu: options?.hideColumnMenu || column.disableColumnMenu,
      }),
    );
    const width = Math.max(baseWidth, floor);
    return total + (maxWidth !== undefined ? Math.min(width, maxWidth) : width);
  }, 0);

/** A status column's `valueFormatter` sets the chip text; the raw value still picks its colour. */
const statusLabel = <R extends GridValidRowModel>(
  params: GridRenderCellParams<R>,
): string | undefined =>
  typeof params.formattedValue === 'string' ? params.formattedValue : undefined;

export default useGeneratikTableColumns;
